/**
 * MeshCore Node Decoder
 * Port of Python decoder to JavaScript
 */

export function hexToBytes(hexString) {
  // Remove meshcore:// prefix if present
  if (hexString.startsWith('meshcore://')) {
    hexString = hexString.substring(11);
  }
  
  // Remove any whitespace or separators
  hexString = hexString.replace(/[\s:-]/g, '');
  
  if (hexString.length % 2 !== 0) {
    throw new Error('Hex string must have even length');
  }
  
  const bytes = new Uint8Array(hexString.length / 2);
  for (let i = 0; i < hexString.length; i += 2) {
    bytes[i / 2] = parseInt(hexString.substr(i, 2), 16);
  }
  return bytes;
}

export function decodePacketHeader(headerByte) {
  const routeType = headerByte & 0x03;
  const payloadType = (headerByte >> 2) & 0x0F;
  const payloadVersion = (headerByte >> 6) & 0x03;
  
  const routeTypes = {
    0x00: "TRANSPORT_FLOOD",
    0x01: "FLOOD",
    0x02: "DIRECT",
    0x03: "TRANSPORT_DIRECT"
  };
  
  const payloadTypes = {
    0x00: "REQ (Request)",
    0x01: "RESPONSE",
    0x02: "TXT_MSG (Text Message)",
    0x03: "ACK",
    0x04: "ADVERT (Node Advertisement)",
    0x05: "GRP_TXT (Group Text)",
    0x06: "GRP_DATA (Group Data)",
    0x07: "ANON_REQ (Anonymous Request)",
    0x08: "PATH (Returned Path)",
    0x09: "TRACE",
    0x0A: "MULTIPART",
    0x0F: "RAW_CUSTOM"
  };
  
  return {
    routeType: routeTypes[routeType] || `Unknown (${routeType})`,
    payloadType: payloadTypes[payloadType] || `Unknown (${payloadType})`,
    payloadVersion,
    rawHeader: `0x${headerByte.toString(16).padStart(2, '0').toUpperCase()}`
  };
}

function bytesToInt32LE(bytes, offset) {
  return (bytes[offset] | 
          (bytes[offset + 1] << 8) | 
          (bytes[offset + 2] << 16) | 
          (bytes[offset + 3] << 24)) >>> 0;
}

function bytesToInt32LESignedCoord(bytes, offset) {
  const value = bytes[offset] | 
                (bytes[offset + 1] << 8) | 
                (bytes[offset + 2] << 16) | 
                (bytes[offset + 3] << 24);
  // Convert to signed 32-bit integer
  return value > 0x7FFFFFFF ? value - 0x100000000 : value;
}

export function decodeAppdata(appdata) {
  if (appdata.length < 1) return null;
  
  const flags = appdata[0];
  const data = { flags: `0x${flags.toString(16).padStart(2, '0').toUpperCase()}` };
  let offset = 1;
  
  // Debug: log appdata bytes
  console.log('Appdata bytes:', Array.from(appdata).map(b => '0x' + b.toString(16).padStart(2, '0')).join(' '));
  console.log('Appdata length:', appdata.length);
  console.log('Flags binary:', flags.toString(2).padStart(8, '0'));
  
  // EXPERIMENTAL: Try reading lat/lon assuming they're always both present at fixed positions
  // Flags might indicate something else (validity, precision, etc.)
  if (appdata.length >= 9) {
    const latRaw = bytesToInt32LESignedCoord(appdata, 1);
    const lonRaw = bytesToInt32LESignedCoord(appdata, 5);
    const latDecoded = latRaw / 1000000.0;
    const lonDecoded = lonRaw / 1000000.0;
    console.log('Fixed position decode: lat=' + latDecoded + ', lon=' + lonDecoded);
    
    // Check if these values are in valid ranges
    const validLat = latDecoded >= -90 && latDecoded <= 90;
    const validLon = lonDecoded >= -180 && lonDecoded <= 180;
    
    if (validLat && validLon) {
      console.log('✓ Both coordinates valid! Using fixed position decode.');
      data.latitude = latDecoded;
      data.longitude = lonDecoded;
      offset = 9; // Skip past the coordinates
      
      // Continue with other fields if present
      if (flags & 0x04 && appdata.length >= offset + 2) {
        data.feature1 = appdata[offset] | (appdata[offset + 1] << 8);
        offset += 2;
      }
      if (flags & 0x08 && appdata.length >= offset + 2) {
        data.feature2 = appdata[offset] | (appdata[offset + 1] << 8);
        offset += 2;
      }
      
      // Check for text/name field after coordinates
      if (appdata.length > offset) {
        try {
          const textBytes = appdata.slice(offset);
          const name = new TextDecoder('utf-8', { fatal: false }).decode(textBytes);
          // Remove null bytes and check if it's printable text
          const cleanName = name.replace(/\0/g, '').trim();
          if (cleanName.length > 0 && /^[\x20-\x7E\s]+$/.test(cleanName)) {
            data.name = cleanName;
          }
        } catch (e) {
          // Ignore decode errors
        }
      }
      
      return data;
    }
    console.log('Fixed position invalid. Falling back to flag-based decode.');
  }
  
  // Original flag-based decoding
  if (flags & 0x01 && appdata.length >= offset + 4) {
    const latRaw = bytesToInt32LESignedCoord(appdata, offset);
    data.latitude = latRaw / 1000000.0;
    offset += 4;
  }
  
  if (flags & 0x02 && appdata.length >= offset + 4) {
    const lonRaw = bytesToInt32LESignedCoord(appdata, offset);
    data.longitude = lonRaw / 1000000.0;
    offset += 4;
  }
  
  if (flags & 0x04 && appdata.length >= offset + 2) {
    // Feature 1
    const feature1 = appdata[offset] | (appdata[offset + 1] << 8);
    data.feature1 = feature1;
    offset += 2;
  }
  
  if (flags & 0x08 && appdata.length >= offset + 2) {
    // Feature 2
    const feature2 = appdata[offset] | (appdata[offset + 1] << 8);
    data.feature2 = feature2;
    offset += 2;
  }
  
  return data;
}

export function decodeNodeAdvertisement(data) {
  if (data.length < 100) return null;
  
  // Extract components
  const publicKey = data.slice(0, 32);
  const timestamp = bytesToInt32LE(data, 32);
  const signature = data.slice(36, 100);
  const appdata = data.length > 100 ? data.slice(100) : new Uint8Array(0);
  
  // Convert timestamp to human readable
  let timestampStr;
  try {
    const date = new Date(timestamp * 1000);
    timestampStr = date.toISOString().replace('T', ' ').split('.')[0] + ' UTC';
  } catch (e) {
    timestampStr = `Invalid timestamp (${timestamp})`;
  }
  
  const result = {
    publicKey: Array.from(publicKey).map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase(),
    nodeHash: `0x${publicKey[0].toString(16).padStart(2, '0').toUpperCase()}`,
    timestamp,
    timestampStr,
    signature: Array.from(signature).map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase(),
    appdataLength: appdata.length
  };
  
  // Decode appdata if present
  if (appdata.length > 0) {
    result.appdata = decodeAppdata(appdata);
  }
  
  return result;
}

export function decodeMeshcoreData(hexString) {
  try {
    const data = hexToBytes(hexString);
    
    if (data.length === 0) {
      return { error: 'Empty data' };
    }
    
    const result = {};
    
    // Try to decode as a packet
    const headerInfo = decodePacketHeader(data[0]);
    result.header = headerInfo;
    
    // If it looks like a node advertisement, decode it
    if (headerInfo.payloadType.includes('ADVERT')) {
      let payloadStart = 1;
      
      if (headerInfo.routeType.includes('TRANSPORT')) {
        payloadStart += 4; // Skip transport codes
      }
      
      if (data.length > payloadStart) {
        const pathLen = data[payloadStart];
        payloadStart += 1 + pathLen; // Skip path
        
        if (data.length > payloadStart) {
          const advertData = decodeNodeAdvertisement(data.slice(payloadStart));
          if (advertData) {
            result.advertisement = advertData;
            return result;
          }
        }
      }
    }
    
    // Try direct decode as node advertisement
    if (data.length >= 100) {
      const advertData = decodeNodeAdvertisement(data);
      if (advertData) {
        result.advertisement = advertData;
        return result;
      }
    }
    
    return result;
  } catch (error) {
    return { error: error.message };
  }
}

/**
 * Extract coordinates from a decoded node
 * @param {string} nodeId - Hex string node ID
 * @returns {object|null} - { latitude, longitude, nodeHash } or null
 */
export function extractNodeCoordinates(nodeId) {
  try {
    const decoded = decodeMeshcoreData(nodeId);
    
    console.log('Decoded data:', decoded); // Debug logging
    
    if (decoded.error) {
      console.error('Decode error:', decoded.error);
      return null;
    }
    
    if (decoded.advertisement) {
      const ad = decoded.advertisement;
      let latitude, longitude;
      
      // Check if coordinates are in appdata
      if (ad.appdata) {
        latitude = ad.appdata.latitude;
        longitude = ad.appdata.longitude;
      }
      
      // Check if we have BOTH coordinates
      const hasLatitude = latitude !== undefined && latitude !== null;
      const hasLongitude = longitude !== undefined && longitude !== null;
      
      if (hasLatitude && hasLongitude) {
        // We have both coordinates!
        return {
          latitude,
          longitude,
          nodeHash: ad.nodeHash || '0x00',
          publicKey: ad.publicKey || 'UNKNOWN',
          timestamp: ad.timestampStr || 'Unknown',
          name: ad.appdata?.name || null
        };
      }
      
      // Missing one or both coordinates
      console.log(`Missing coordinates - lat: ${hasLatitude ? 'yes' : 'no'}, lon: ${hasLongitude ? 'yes' : 'no'}`);
      console.log(`Values - lat: ${latitude}, lon: ${longitude}`);
      
      return {
        latitude: hasLatitude ? latitude : null,
        longitude: hasLongitude ? longitude : null,
        nodeHash: ad.nodeHash || '0x00',
        publicKey: ad.publicKey || 'UNKNOWN',
        timestamp: ad.timestampStr || 'Unknown',
        noLocation: true,
        partialLocation: hasLatitude || hasLongitude
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error extracting coordinates:', error);
    return null;
  }
}

